import { useState, useCallback, useMemo, useEffect } from 'react';

/**
 * Custom hook for managing hierarchical tree state
 *
 * Handles selection, expansion, search, and tree operations
 * Optimized for large trees (1000+ nodes)
 *
 * Features:
 * - Cascade selection (parent → all children)
 * - Deduplication (remove children when parent selected)
 * - Indeterminate state (partial selection)
 * - Search/filter with auto-expand
 * - Expand/collapse operations
 */
export const useTreeState = ({
  treeData = [],
  initialSelected = [],
  initialExpanded = [],
  cascadeSelection = true,
  deduplicateOnCascade = true,
  onSelectionChange,
  getNodeId = (node) => node.id
}) => {
  const [selectedItems, setSelectedItems] = useState(initialSelected);
  const [expandedNodes, setExpandedNodes] = useState(initialExpanded);
  const [searchQuery, setSearchQuery] = useState('');

  // Build node map for quick lookups - O(n) time, O(n) space
  const nodeMap = useMemo(() => {
    const map = new Map();

    const traverse = (nodes) => {
      nodes.forEach(node => {
        const nodeId = getNodeId(node);
        map.set(nodeId, node);
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      });
    };

    traverse(treeData);
    return map;
  }, [treeData, getNodeId]);

  // Get all child IDs recursively
  const getAllChildIds = useCallback((nodeId) => {
    const node = nodeMap.get(nodeId);
    if (!node) return [];

    let ids = [];

    const traverse = (n) => {
      if (n.children && n.children.length > 0) {
        n.children.forEach(child => {
          const childId = getNodeId(child);
          ids.push(childId);
          traverse(child);
        });
      }
    };

    traverse(node);
    return ids;
  }, [nodeMap, getNodeId]);

  // Get parent ID
  const getParentId = useCallback((nodeId) => {
    for (const [id, node] of nodeMap.entries()) {
      if (node.children && node.children.some(child => getNodeId(child) === nodeId)) {
        return id;
      }
    }
    return null;
  }, [nodeMap, getNodeId]);

  // Check if node is checked (either directly or via parent selection with deduplication)
  const isNodeChecked = useCallback((nodeId) => {
    // Direct check - node itself is selected
    if (selectedItems.includes(nodeId)) return true;

    // If deduplication is enabled, check if any parent is selected
    // (because parent selection means all children are implicitly selected)
    if (deduplicateOnCascade) {
      let currentId = nodeId;
      // Traverse up the tree to check ancestors
      while (currentId) {
        const parentId = getParentId(currentId);
        if (!parentId) break;
        if (selectedItems.includes(parentId)) return true;
        currentId = parentId;
      }
    }

    return false;
  }, [selectedItems, deduplicateOnCascade, getParentId]);

  // Check if node is indeterminate (some children checked)
  const isNodeIndeterminate = useCallback((nodeId) => {
    const node = nodeMap.get(nodeId);
    if (!node || !node.children || node.children.length === 0) return false;

    // If this node itself is selected, it's not indeterminate (all children selected)
    if (selectedItems.includes(nodeId)) return false;

    const childIds = getAllChildIds(nodeId);
    // Use isNodeChecked to properly check children (accounts for parent selection)
    const checkedChildren = childIds.filter(id => isNodeChecked(id));

    return checkedChildren.length > 0 && checkedChildren.length < childIds.length;
  }, [nodeMap, getAllChildIds, selectedItems, isNodeChecked]);

  // Toggle node selection
  const toggleNode = useCallback((nodeId) => {
    console.log('nodeId', nodeId);
    const node = nodeMap.get(nodeId);
    if (!node) return;

    let newSelection = [...selectedItems];
    // Use isNodeChecked to account for parent selection with deduplication
    const isChecked = isNodeChecked(nodeId);
    console.log(newSelection.length);
     console.log('isChecked', {isChecked, nodeId,  newSelection, checked: newSelection.includes(nodeId)});
    if (isChecked) {
      // Check if this node is directly in the selection or selected via parent
      const isDirectlySelected = newSelection.includes(nodeId);

      if (isDirectlySelected) {
        // Uncheck: Remove this node and all children
        if (cascadeSelection) {
          const childIds = getAllChildIds(nodeId);
          newSelection = newSelection.filter(id =>
            id !== nodeId && !childIds.includes(id)
          );
        } else {
          newSelection = newSelection.filter(id => id !== nodeId);
        }
      } else {
        // Node is checked via parent selection - need to split the parent selection
        // Find the parent that's causing this node to be selected
        let selectedParentId = null;
        let currentId = nodeId;
        while (currentId) {
          const parentId = getParentId(currentId);
          if (!parentId) break;
          if (newSelection.includes(parentId)) {
            selectedParentId = parentId;
            break;
          }
          currentId = parentId;
        }

        if (selectedParentId) {
          // Remove the parent
          newSelection = newSelection.filter(id => id !== selectedParentId);

          // Add back all children of the parent EXCEPT the node being unchecked and its descendants
          const parentNode = nodeMap.get(selectedParentId);
          const nodeToUncheckChildren = getAllChildIds(nodeId);

          if (parentNode && parentNode.children) {
            parentNode.children.forEach(child => {
              const childId = getNodeId(child);
              // Skip the node being unchecked
              if (childId === nodeId) return;

              // Add this sibling
              if (!newSelection.includes(childId)) {
                newSelection.push(childId);
              }

              // If cascade and deduplicate, don't add grandchildren (they're implicit)
              if (!deduplicateOnCascade) {
                const grandchildren = getAllChildIds(childId);
                grandchildren.forEach(gcId => {
                  if (!newSelection.includes(gcId)) {
                    newSelection.push(gcId);
                  }
                });
              }
            });
          }
        }
      }
    } else {
      // Check: Add this node
      newSelection.push(nodeId);

      if (cascadeSelection) {
        // Add all children
        const childIds = getAllChildIds(nodeId);
        childIds.forEach(childId => {
          if (!newSelection.includes(childId)) {
            newSelection.push(childId);
          }
        });

        // Deduplication: If adding parent, remove specific children
        // This implements "dashboard.*" behavior - store parent, remove children
        if (deduplicateOnCascade && childIds.length > 0) {
          newSelection = newSelection.filter(id => !childIds.includes(id));
        }
      }
    }

    setSelectedItems(newSelection);
    onSelectionChange?.(newSelection);
  }, [nodeMap, selectedItems, cascadeSelection, deduplicateOnCascade, getAllChildIds, onSelectionChange, isNodeChecked, getParentId, getNodeId]);

  // Select multiple nodes
  const selectNodes = useCallback((nodeIds) => {
    setSelectedItems(nodeIds);
    onSelectionChange?.(nodeIds);
  }, [onSelectionChange]);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedItems([]);
    onSelectionChange?.([]);
  }, [onSelectionChange]);

  // Expand node
  const expandNode = useCallback((nodeId) => {
    if (!expandedNodes.includes(nodeId)) {
      setExpandedNodes([...expandedNodes, nodeId]);
    }
  }, [expandedNodes]);

  // Collapse node
  const collapseNode = useCallback((nodeId) => {
    setExpandedNodes(expandedNodes.filter(id => id !== nodeId));
  }, [expandedNodes]);

  // Expand all nodes
  const expandAll = useCallback(() => {
    const allIds = Array.from(nodeMap.keys());
    setExpandedNodes(allIds);
  }, [nodeMap]);

  // Collapse all nodes
  const collapseAll = useCallback(() => {
    setExpandedNodes([]);
  }, []);

  // Filter tree based on search
  const filterTree = useCallback((nodes, query) => {
    if (!query) return nodes;

    const lowerQuery = query.toLowerCase();

    return nodes.reduce((acc, node) => {
      const labelMatches = node.name?.toLowerCase().includes(lowerQuery) ||
                          node.path?.toLowerCase().includes(lowerQuery) ||
                          node.description?.toLowerCase().includes(lowerQuery);

      let filteredChildren = [];
      if (node.children && node.children.length > 0) {
        filteredChildren = filterTree(node.children, query);
      }

      if (labelMatches || filteredChildren.length > 0) {
        acc.push({
          ...node,
          children: filteredChildren
        });
      }

      return acc;
    }, []);
  }, []);

  // Get filtered tree
  const filteredTree = useMemo(() => {
    return filterTree(treeData, searchQuery);
  }, [treeData, searchQuery, filterTree]);

  // Auto-expand filtered results
  useEffect(() => {
    if (searchQuery && filteredTree.length > 0) {
      const allIds = Array.from(nodeMap.keys());
      setExpandedNodes(allIds);
    }
  }, [searchQuery, filteredTree, nodeMap]);

  return {
    // State
    selectedItems,
    expandedNodes,
    searchQuery,
    filteredTree,
    nodeMap,

    // Selection
    toggleNode,
    selectNodes,
    clearSelection,
    isNodeChecked,
    isNodeIndeterminate,

    // Expansion
    expandNode,
    collapseNode,
    expandAll,
    collapseAll,
    setExpandedNodes,

    // Search
    setSearchQuery,

    // Utilities
    getAllChildIds,
    getParentId
  };
};

export default useTreeState;
